import React from 'react';
import { Box, Container, Typography, Button } from '@mui/material';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error('Error caught by boundary:', error, errorInfo);
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null });
    // replace() (not href=) so the crashed page is dropped from history and
    // Back doesn't return to it and re-throw. Full document nav is intentional:
    // it reloads a clean React tree rather than routing on top of a broken one.
    window.location.replace('/home');
  };

  render() {
    if (this.state.hasError) {
      return (
        <Box
          sx={{
            minHeight: '100vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: '#f8f9fe',
          }}
        >
          <Container maxWidth="sm" sx={{ textAlign: 'center' }}>
            <ErrorOutlineIcon
              sx={{
                fontSize: '4rem',
                color: 'error.main',
                mb: 2,
              }}
            />
            <Typography variant="h4" sx={{ fontWeight: 800, color: 'dark.main', mb: 1.5 }}>
              Oops, something went wrong
            </Typography>
            <Typography
              variant="body1"
              sx={{ color: 'text.secondary', mb: 4, lineHeight: 1.6 }}
            >
              We encountered an unexpected error. Please try refreshing the page or return to the home page.
            </Typography>
            {process.env.NODE_ENV === 'development' && this.state.error && (
              <Box
                sx={{
                  mb: 3,
                  p: 2,
                  backgroundColor: '#fff3cd',
                  border: '1px solid #ffc107',
                  borderRadius: '0.375rem',
                  textAlign: 'left',
                }}
              >
                <Typography
                  variant="caption"
                  sx={{ color: 'text.primary', fontFamily: 'monospace', fontSize: '0.75rem' }}
                >
                  {this.state.error.toString()}
                </Typography>
              </Box>
            )}
            <Button
              variant="contained"
              size="large"
              onClick={this.handleReset}
              sx={{
                backgroundColor: 'primary.main',
                px: 4,
                py: 1.25,
                '&:hover': { backgroundColor: 'primary.extraDark' },
              }}
            >
              Back to Home
            </Button>
          </Container>
        </Box>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
